import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Copy, KeyRound, PauseCircle, RefreshCw, Trash2 } from 'lucide-react';
import { Button, ConfirmDialog, Drawer, ErrorCard, SkeletonTable, cn } from '@/components/ui';
import { PageHeader, Section, Stat } from '@/components/layout/PageLayout';
import { LicenseStatusBadge } from '@/components/tenants/LicenseStatusBadge';
import { LicenseKeyRevealDialog } from '@/components/tenants/LicenseKeyRevealDialog';
import {
  useDeleteLicense, useDeleteTenant, useIssueLicense, useRegenerateLicenseKey, useTenant, useToggleLicenseEnabled,
  useToggleTenantStatus, useUpdateLicense, useUpdateTenant,
} from '@/api/tenants';
import { getErrorMessage } from '@/lib/errors';
import { showToast } from '@/lib/toast';

function toDateInputValue(iso: string): string {
  return iso.slice(0, 10);
}

function IssueLicenseForm({
  tenantId, onIssued,
}: { tenantId: string; onIssued: (licenseKey: string) => void }) {
  const [endpointLimit, setEndpointLimit] = useState(10);
  const [expiryDate, setExpiryDate] = useState('');
  const { mutate, isPending, error } = useIssueLicense(tenantId);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate(
      { endpointLimit, expiryDate: new Date(expiryDate).toISOString() },
      { onSuccess: (result) => onIssued(result.licenseKey!) },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger">
          {getErrorMessage(error)}
        </div>
      )}
      <div>
        <label htmlFor="endpointLimit" className="mb-1.5 block text-xs font-medium text-text-secondary">
          Endpoint limit
        </label>
        <input
          id="endpointLimit"
          type="number"
          min={0}
          required
          value={endpointLimit}
          onChange={(e) => setEndpointLimit(Number(e.target.value))}
          className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-text-primary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
        />
      </div>
      <div>
        <label htmlFor="expiryDate" className="mb-1.5 block text-xs font-medium text-text-secondary">
          Expiry date
        </label>
        <input
          id="expiryDate"
          type="date"
          required
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-text-primary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
        />
      </div>
      <Button type="submit" variant="primary" size="md" fullWidth loading={isPending}>
        Issue license
      </Button>
    </form>
  );
}

export function TenantDetailPage() {
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();

  const [issueLicenseOpen, setIssueLicenseOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    'delete-tenant' | 'revoke-license' | 'regenerate-key' | 'toggle-status' | 'toggle-license' | null
  >(null);

  // Inline edit state — Tenant details and License fields are edited in place
  // on this page rather than in a side panel.
  const [isEditingTenant, setIsEditingTenant] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [urlDraft, setUrlDraft] = useState('');
  const [isEditingLicense, setIsEditingLicense] = useState(false);
  const [licenseDraft, setLicenseDraft] = useState({ endpointLimit: 0, endpointsUsed: 0, expiryDate: '' });

  const { data: tenant, isLoading, isError, error, refetch } = useTenant(tenantId);
  const { mutate: deleteTenant, isPending: isDeletingTenant } = useDeleteTenant();
  const { mutate: deleteLicense, isPending: isRevokingLicense } = useDeleteLicense(tenantId);
  const { mutate: regenerateKey, isPending: isRegenerating } = useRegenerateLicenseKey(tenantId);
  const { mutate: toggleStatus, isPending: isTogglingStatus } = useToggleTenantStatus();
  const { mutate: toggleLicenseEnabled, isPending: isTogglingLicense } = useToggleLicenseEnabled(tenantId);
  const { mutate: updateTenant, isPending: isSavingTenant, error: updateTenantError } = useUpdateTenant(tenantId);
  const { mutate: updateLicense, isPending: isSavingLicense, error: updateLicenseError } = useUpdateLicense(tenantId);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader eyebrow="Governance" title="Tenant" />
        <SkeletonTable rows={5} />
      </div>
    );
  }

  if (isError || !tenant) {
    return <ErrorCard title="Failed to load tenant" message={getErrorMessage(error)} onRetry={() => void refetch()} />;
  }

  const startEditTenant = () => { setNameDraft(tenant.name); setUrlDraft(tenant.url); setIsEditingTenant(true); };
  const cancelEditTenant = () => setIsEditingTenant(false);
  const saveTenant = (e: FormEvent) => {
    e.preventDefault();
    updateTenant(
      { name: nameDraft, url: urlDraft },
      { onSuccess: () => { showToast('Tenant updated successfully'); setIsEditingTenant(false); } },
    );
  };

  const startEditLicense = () => {
    if (!tenant.license) return;
    setLicenseDraft({
      endpointLimit: tenant.license.endpointLimit,
      endpointsUsed: tenant.license.endpointsUsed,
      expiryDate: toDateInputValue(tenant.license.expiryDate),
    });
    setIsEditingLicense(true);
  };
  const cancelEditLicense = () => setIsEditingLicense(false);
  const saveLicense = (e: FormEvent) => {
    e.preventDefault();
    updateLicense(
      {
        endpointLimit: licenseDraft.endpointLimit,
        endpointsUsed: licenseDraft.endpointsUsed,
        expiryDate: new Date(licenseDraft.expiryDate).toISOString(),
      },
      { onSuccess: () => { showToast('License updated successfully'); setIsEditingLicense(false); } },
    );
  };

  const closeConfirm = () => setConfirmAction(null);

  const handleConfirm = () => {
    if (confirmAction === 'delete-tenant') {
      deleteTenant(tenant.tenantId, {
        onSuccess: () => {
          showToast('Tenant deleted successfully');
          navigate('/tenant', { replace: true });
        },
        onError: closeConfirm,
      });
    } else if (confirmAction === 'revoke-license') {
      deleteLicense(undefined, {
        onSuccess: () => { showToast('License revoked successfully'); closeConfirm(); },
        onError: closeConfirm,
      });
    } else if (confirmAction === 'regenerate-key') {
      regenerateKey(undefined, {
        onSuccess: (result) => { setRevealedKey(result.licenseKey); closeConfirm(); },
        onError: closeConfirm,
      });
    } else if (confirmAction === 'toggle-status') {
      toggleStatus(
        { tenantId: tenant.tenantId, status: tenant.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE' },
        {
          onSuccess: () => {
            showToast(tenant.status === 'ACTIVE' ? 'Tenant suspended' : 'Tenant activated');
            closeConfirm();
          },
          onError: closeConfirm,
        },
      );
    } else if (confirmAction === 'toggle-license' && tenant.license) {
      toggleLicenseEnabled(!tenant.license.enabled, {
        onSuccess: () => {
          showToast(tenant.license!.enabled ? 'License disabled' : 'License enabled');
          closeConfirm();
        },
        onError: closeConfirm,
      });
    }
  };

  const isConfirmLoading =
    isDeletingTenant || isRevokingLicense || isRegenerating || isTogglingStatus || isTogglingLicense;

  function confirmDialogCopy() {
    // Non-null assertion: the `if (isError || !tenant) return ...` guard above
    // guarantees tenant is defined by the time this runs, but that narrowing
    // doesn't carry into this nested function, so TS still sees it as optional.
    const t = tenant!;
    switch (confirmAction) {
      case 'delete-tenant':
        return {
          title: `Delete ${t.name}?`,
          description: t.license
            ? "This tenant has an active license — remove it first before deleting."
            : 'This action cannot be undone.',
          confirmLabel: 'Delete',
          destructive: true,
        };
      case 'revoke-license':
        return {
          title: 'Revoke this license?',
          description: 'This immediately invalidates the license key. This action cannot be undone.',
          confirmLabel: 'Revoke',
          destructive: true,
        };
      case 'regenerate-key':
        return {
          title: 'Regenerate license key?',
          description: 'The current key will stop working immediately — anything using it will need the new key.',
          confirmLabel: 'Regenerate',
          destructive: false,
        };
      case 'toggle-status':
        return t.status === 'ACTIVE'
          ? {
              title: `Suspend ${t.name}?`,
              description: 'This freezes the tenant without touching its license — you can reactivate it at any time.',
              confirmLabel: 'Suspend',
              destructive: true,
            }
          : {
              title: `Activate ${t.name}?`,
              description: 'This restores the tenant to active status.',
              confirmLabel: 'Activate',
              destructive: false,
            };
      case 'toggle-license':
        return t.license?.enabled
          ? {
              title: 'Disable this license?',
              description: 'Endpoints relying on this license may be affected while it is disabled. You can re-enable it at any time.',
              confirmLabel: 'Disable',
              destructive: true,
            }
          : {
              title: 'Enable this license?',
              description: 'This restores the license to its normal status based on its expiry date.',
              confirmLabel: 'Enable',
              destructive: false,
            };
      default:
        return { title: '', description: '', confirmLabel: 'Confirm', destructive: false };
    }
  }

  const confirmCopy = confirmDialogCopy();

  const inputCls =
    'w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-text-primary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20';

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Governance"
        title={tenant.name}
        description={
          <button onClick={() => navigate('/tenant')} className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-text-primary">
            <ArrowLeft className="h-3 w-3" /> Back to tenants
          </button>
        }
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={tenant.status === 'ACTIVE' ? <PauseCircle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              onClick={() => setConfirmAction('toggle-status')}
            >
              {tenant.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
            </Button>
            <Button
              variant="danger"
              size="sm"
              leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
              onClick={() => setConfirmAction('delete-tenant')}
            >
              Delete tenant
            </Button>
          </div>
        }
      />

      <Section
        title="Tenant details"
        actions={
          isEditingTenant ? (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={cancelEditTenant} disabled={isSavingTenant}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={saveTenant} loading={isSavingTenant}>Save</Button>
            </div>
          ) : (
            <Button variant="secondary" size="sm" onClick={startEditTenant}>Edit</Button>
          )
        }
      >
        {isEditingTenant && updateTenantError && (
          <div className="mb-3 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger">
            {getErrorMessage(updateTenantError)}
          </div>
        )}
        <form onSubmit={saveTenant} className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Stat label="Tenant ID" value={<span className="font-mono">{tenant.tenantId}</span>} />
          {isEditingTenant ? (
            <div className="space-y-1">
              <label htmlFor="detail-edit-name" className="block text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                Name
              </label>
              <input
                id="detail-edit-name"
                required
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                className={inputCls}
              />
            </div>
          ) : (
            <Stat label="Name" value={tenant.name} />
          )}
          {isEditingTenant ? (
            <div className="space-y-1">
              <label htmlFor="detail-edit-url" className="block text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                URL
              </label>
              <input
                id="detail-edit-url"
                type="text"
                required
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                className={inputCls}
              />
            </div>
          ) : (
            <Stat label="URL" value={<span className="truncate">{tenant.url}</span>} />
          )}
          <Stat
            label="Status"
            value={
              <span
                className={cn(
                  'inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium',
                  tenant.status === 'ACTIVE'
                    ? 'border-state-success/40 bg-state-success/10 text-state-success'
                    : 'border-state-warning/40 bg-state-warning/10 text-state-warning',
                )}
              >
                {tenant.status === 'ACTIVE' ? 'Active' : 'Suspended'}
              </span>
            }
          />
          <Stat label="Created" value={new Date(tenant.createdAt).toLocaleDateString()} />
        </form>
      </Section>

      <Section
        title="License"
        actions={
          tenant.license ? (
            isEditingLicense ? (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={cancelEditLicense} disabled={isSavingLicense}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={saveLicense} loading={isSavingLicense}>Save</Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={tenant.license.enabled ? <PauseCircle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  onClick={() => setConfirmAction('toggle-license')}
                >
                  {tenant.license.enabled ? 'Disable' : 'Enable'}
                </Button>
                <Button variant="secondary" size="sm" onClick={startEditLicense}>
                  Edit
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<RefreshCw className="h-3.5 w-3.5" />}
                  onClick={() => setConfirmAction('regenerate-key')}
                >
                  Regenerate key
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
                  onClick={() => setConfirmAction('revoke-license')}
                >
                  Revoke
                </Button>
              </div>
            )
          ) : (
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<KeyRound className="h-3.5 w-3.5" />}
              onClick={() => setIssueLicenseOpen(true)}
            >
              Issue license
            </Button>
          )
        }
      >
        {tenant.license ? (
          <>
            {isEditingLicense && updateLicenseError && (
              <div className="mb-3 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger">
                {getErrorMessage(updateLicenseError)}
              </div>
            )}
            <form onSubmit={saveLicense} className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat label="Status" value={<LicenseStatusBadge status={tenant.license.status} />} />
              {isEditingLicense ? (
                <div className="space-y-1">
                  <label htmlFor="detail-edit-endpointLimit" className="block text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                    Endpoint limit
                  </label>
                  <input
                    id="detail-edit-endpointLimit"
                    type="number"
                    min={0}
                    required
                    autoFocus
                    value={licenseDraft.endpointLimit}
                    onChange={(e) => setLicenseDraft((d) => ({ ...d, endpointLimit: Number(e.target.value) }))}
                    className={inputCls}
                  />
                </div>
              ) : (
                <Stat label="Endpoint limit" value={tenant.license.endpointLimit} />
              )}
              {isEditingLicense ? (
                <div className="space-y-1">
                  <label htmlFor="detail-edit-endpointsUsed" className="block text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                    Endpoints used
                  </label>
                  <input
                    id="detail-edit-endpointsUsed"
                    type="number"
                    min={0}
                    required
                    value={licenseDraft.endpointsUsed}
                    onChange={(e) => setLicenseDraft((d) => ({ ...d, endpointsUsed: Number(e.target.value) }))}
                    className={inputCls}
                  />
                </div>
              ) : (
                <Stat label="Endpoints used" value={tenant.license.endpointsUsed} />
              )}
              {isEditingLicense ? (
                <div className="space-y-1">
                  <label htmlFor="detail-edit-expiryDate" className="block text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                    Expiry date
                  </label>
                  <input
                    id="detail-edit-expiryDate"
                    type="date"
                    required
                    value={licenseDraft.expiryDate}
                    onChange={(e) => setLicenseDraft((d) => ({ ...d, expiryDate: e.target.value }))}
                    className={inputCls}
                  />
                </div>
              ) : (
                <Stat label="Expiry date" value={new Date(tenant.license.expiryDate).toLocaleDateString()} />
              )}
              <Stat
                label="License key"
                value={
                  <button
                    type="button"
                    title={tenant.license.licenseKey ? 'Copy full license key' : 'Regenerate the key to enable copying'}
                    className="group/key inline-flex cursor-pointer items-center gap-1.5 font-mono"
                    onClick={async () => {
                      const key = tenant.license!.licenseKey;
                      if (!key) {
                        showToast('Regenerate the key to enable copying the full value', 'error');
                        return;
                      }
                      await navigator.clipboard.writeText(key);
                      showToast('License key copied to clipboard');
                    }}
                  >
                    •••• {tenant.license.licenseKeyLastFour}
                    <Copy className="h-3 w-3 text-text-tertiary opacity-0 transition-opacity group-hover/key:opacity-100" />
                  </button>
                }
              />
            </form>
          </>
        ) : (
          <p className="text-xs text-text-tertiary">This tenant has no license yet.</p>
        )}
      </Section>

      <Drawer open={issueLicenseOpen} onClose={() => setIssueLicenseOpen(false)} title="Issue license" size="sm">
        <IssueLicenseForm
          tenantId={tenant.tenantId}
          onIssued={(licenseKey) => {
            setIssueLicenseOpen(false);
            setRevealedKey(licenseKey);
          }}
        />
      </Drawer>

      <LicenseKeyRevealDialog licenseKey={revealedKey} onClose={() => setRevealedKey(null)} />

      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmCopy.title}
        description={confirmCopy.description}
        confirmLabel={confirmCopy.confirmLabel}
        destructive={confirmCopy.destructive}
        loading={isConfirmLoading}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
}
